<template>
  <div class="summary-compare">
    <data-filter ref="dataFilter" :on-apply-filter="onApplyFilter" hide-interval hide-report />
    <!-- mode bertumpuk (layar sempit): slot ikut kegulir, cuma legenda yang nempel -->
    <div
      ref="barWrap"
      :class="['summary-compare__band', { 'summary-compare__band--pinned': pinBar }]"
      :style="pinBar ? { top: `${stickyTop}px` } : null"
    >
      <compare-bar
        :assets="assets"
        :values="rights"
        :max="maxComparisons"
        :left-mass-id="leftMassId"
        :warning="barWarning"
        @change="onRightsChange"
        @guide="$refs.guide.open()"
      />
    </div>
    <div
      v-if="showCards"
      class="summary-compare__band summary-compare__band--pinned summary-compare__band--legend"
      :style="{ top: `${legendTop}px` }"
    >
      <metric-group-card
        header-only
        :columns="columns"
        :warn-flags="warnFlags"
        :stacked="stacked"
      />
    </div>

    <div v-if="isLoading" class="text-center py-5"><b-spinner label="Loading" /></div>
    <div v-else-if="!leftAsset" class="text-muted text-center py-5">Pick a vessel to start.</div>
    <div v-else-if="allFailed" class="text-center py-5">
      <p class="text-muted">Failed to load summary.</p>
      <button class="btn btn-mantraz" @click="load">Retry</button>
    </div>
    <div v-else-if="!groups.length" class="text-muted text-center py-5">No summary data for this range.</div>
    <template v-else>
      <metric-group-card
        v-for="group in groups"
        :key="group.key"
        :group="group"
        :columns="columns"
        :warn-flags="warnFlags"
        :warn-text="lengthWarning"
        :empty-text="emptyTextFor(group)"
        :stacked="stacked"
      />
    </template>

    <summary-compare-guide-modal ref="guide" />
  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import filterDeepLink from '@/mixins/filterDeepLink';
import DataFilter from '@/components/DataFilter.vue';
import CompareBar from '@/components/summaryCompare/CompareBar.vue';
import MetricGroupCard from '@/components/summaryCompare/MetricGroupCard.vue';
import SummaryCompareGuideModal from '@/components/summaryCompare/SummaryCompareGuideModal.vue';
import { decodeRights, rightsSearch } from '@/util/summaryCompareQuery';
import {
  resolveRightRange, rangeText, lengthText, lengthsDiffer, sameSide, mergeRows, groupRows, COLUMN_LETTERS,
  requiredTableWidth, columnsThatFit,
} from '@/util/summaryCompareModel';
import { GROUPS } from '@/util/summaryCompareCatalog';
import { fetchSide, devcTypesOf } from '@/util/summaryCompareFetch';

const GUIDE_SEEN_KEY = 'summaryCompareGuideSeen';
const CARD_BODY_PADDING = 30;
const PAGE_PADDING = 32;

// non-custom selalu valid; custom valid kalau start < end
function isValidCustomRange(period) {
  if (!period || period.kind !== 'custom') return true;
  const start = Number(period.startMs);
  const end = Number(period.endMs);
  return Number.isFinite(start) && Number.isFinite(end) && start < end;
}

export default {
  name: 'SummaryCompare',
  mixins: [filterDeepLink],
  filterUrlGroups: ['asset', 'date'],
  components: {
    DataFilter,
    CompareBar,
    MetricGroupCard,
    SummaryCompareGuideModal,
  },
  data() {
    return {
      assetsFailed: false,
      isLoading: false,
      rights: [],
      token: 0,
      baseResult: null,
      comparisonResults: [],
      compareNotice: '',
      // satu snapshot per load(), semua kolom kepotong ke "now" yang sama
      nowMs: Date.now(),
      stickyTop: 0,
      barHeight: 0,
      viewportHeight: window.innerHeight,
      contentWidth: 0,
    };
  },
  computed: {
    ...mapState('asset', ['assets']),
    ...mapGetters('filter', ['currentMassId']),
    leftMassId() {
      return this.currentMassId;
    },
    leftAsset() {
      return (this.assets || []).find((a) => String(a.massId) === String(this.leftMassId)) || null;
    },
    leftRange() {
      const { start, end } = this.$store.getters['filter/date'];
      return { startMs: moment(start).valueOf(), endMs: moment(end).valueOf() };
    },
    // rentang mentah bisa nembus masa depan, potong ke `now` di sini juga
    leftEffectiveRange() {
      return { startMs: this.leftRange.startMs, endMs: Math.min(this.leftRange.endMs, this.nowMs) };
    },
    comparisonAssets() {
      return this.rights.map((r) => {
        const massId = r.massId === null ? this.leftMassId : r.massId;
        return (this.assets || []).find((a) => String(a.massId) === String(massId)) || null;
      });
    },
    comparisonRanges() {
      return this.rights.map((r) => resolveRightRange(r.period, this.leftRange));
    },
    comparisonEffectiveRanges() {
      return this.comparisonRanges.map((r) => ({ startMs: r.startMs, endMs: Math.min(r.endMs, this.nowMs) }));
    },
    // devcType kapal kebetulan sama persis dengan key grup di GROUPS
    expectedKeys() {
      const keys = [this.leftAsset, ...this.comparisonAssets].flatMap((a) => devcTypesOf(a));
      return Array.from(new Set(keys));
    },
    notServedKeys() {
      const results = [this.baseResult, ...this.comparisonResults];
      return new Set(results.flatMap((r) => (r && r.notServed) || []));
    },
    groups() {
      const baseRows = this.baseResult ? this.baseResult.rows : [];
      const comparisonRows = this.comparisonResults.map((r) => (r ? r.rows : null));
      return groupRows(mergeRows(baseRows, ...comparisonRows), GROUPS, this.expectedKeys);
    },
    // Samain sama rantai v-if kartu; header sticky cuma muncul kalau kartunya muncul.
    showCards() {
      return !this.isLoading && !!this.leftAsset && !this.allFailed && this.groups.length > 0;
    },
    warnFlags() {
      return [false, ...this.comparisonEffectiveRanges.map((eff) => lengthsDiffer(this.leftEffectiveRange, eff))];
    },
    lengthWarning() {
      const flagged = [];
      this.warnFlags.forEach((flag, i) => {
        if (flag) flagged.push({ letter: COLUMN_LETTERS[i], range: this.comparisonEffectiveRanges[i - 1] });
      });
      if (!flagged.length) return '';
      const parts = [`A ${lengthText(this.leftEffectiveRange)}`, ...flagged.map((f) => `${f.letter} ${lengthText(f.range)}`)];
      return `Periods have different lengths (${parts.join(', ')}). Totals marked ! are not comparable.`;
    },
    // Bar tampilin dua-duanya kalau ada: notice penghapusan lalu peringatan panjang.
    barWarning() {
      return [this.compareNotice, this.lengthWarning].filter(Boolean).join(' ');
    },
    columns() {
      const base = {
        letter: 'A',
        name: this.leftAsset ? this.leftAsset.massName : 'Unknown vessel',
        rangeText: rangeText(this.leftEffectiveRange),
        status: this.baseResult ? this.baseResult.status : null,
        isBase: true,
      };
      const comparisons = this.rights.map((r, i) => {
        const asset = this.comparisonAssets[i];
        const result = this.comparisonResults[i];
        return {
          letter: COLUMN_LETTERS[i + 1],
          name: asset ? asset.massName : 'Unknown vessel',
          rangeText: rangeText(this.comparisonEffectiveRanges[i]),
          status: result ? result.status : null,
          isBase: false,
        };
      });
      return [base, ...comparisons];
    },
    allFailed() {
      const baseFailed = !!this.baseResult && this.baseResult.status === 'failed';
      return baseFailed && this.comparisonResults.every((r) => !r || r.status === 'failed');
    },
    tableWidth() {
      return this.contentWidth - CARD_BODY_PADDING - PAGE_PADDING;
    },
    stacked() {
      const n = this.columns.length;
      return n > 1 && this.tableWidth < requiredTableWidth(n);
    },
    // tombol Add dibatasi kolom yang muat; link yang udah lebih banyak tetap ditampilkan (bertumpuk)
    maxComparisons() {
      return columnsThatFit(this.tableWidth) - 1;
    },
    // bar ikut nempel cuma kalau gak bertumpuk dan gak kepanjangan (slot banyak = kegulir, legenda aja yang nempel)
    pinBar() {
      return !this.stacked && this.barHeight <= this.viewportHeight * 0.25;
    },
    legendTop() {
      return this.stickyTop + (this.pinBar ? this.barHeight : 0);
    },
  },
  async mounted() {
    this.measureStickyTop();
    this.measureLayout();
    window.addEventListener('resize', this.measureStickyTop);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.measureLayout);
      this.resizeObserver.observe(this.$el);
      this.resizeObserver.observe(this.$refs.barWrap);
    } else {
      window.addEventListener('resize', this.measureLayout);
    }
    this.isLoading = true;
    try {
      if (!this.assets.length) await this.$store.dispatch('asset/getAssetsData');
      // getAssetsData selalu ngisi `devices` array (bisa kosong), jadi cek isinya.
      if (this.assets.some((a) => !a.devices || !a.devices.length)) await this.$store.dispatch('asset/getAssetsDevices');
    } catch (e) {
      this.assetsFailed = true;
      console.warn('[summary compare] muat aset gagal', e && e.message);
      this.$toast.error('Failed to load vessels.');
    }

    // massId dari URL kebaca string, samain ke massId asli (number)
    const currentMassId = this.$store.getters['filter/currentMassId'];
    const assetList = Array.isArray(this.assets) ? this.assets : Object.values(this.assets || []);
    const matched = assetList.find((a) => String(a.massId) === String(currentMassId));
    if (matched && matched.massId !== currentMassId) {
      await this.$store.dispatch('filter/setCurrentMassId', matched.massId);
    }

    this.rights = decodeRights(window.location.search);
    await this.load();
    this.measureStickyTop();
    this.measureLayout();
    this.syncFilterUrl();
    // panduan muncul sendiri sekali per browser
    try {
      if (!localStorage.getItem(GUIDE_SEEN_KEY)) {
        localStorage.setItem(GUIDE_SEEN_KEY, '1');
        this.$nextTick(() => {
          if (this.$refs.guide) this.$refs.guide.open();
        });
      }
    } catch (e) {
      // storage diblok, gapapa
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measureStickyTop);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.measureLayout);
    }
  },
  methods: {
    // Numpuk di bawah navbar + DataFilter yang dua-duanya udah sticky; tingginya bisa beda di layar kecil.
    measureStickyTop() {
      const nav = document.querySelector('nav.navbar');
      const navHeight = nav && ['sticky', 'fixed'].includes(getComputedStyle(nav).position) ? nav.offsetHeight : 0;
      const filter = this.$refs.dataFilter && this.$refs.dataFilter.$el;
      const filterHeight = filter && getComputedStyle(filter).position === 'sticky' ? filter.offsetHeight : 0;
      this.stickyTop = navHeight + filterHeight;
      this.viewportHeight = window.innerHeight;
    },
    // lebar konten -> stacked; tinggi bar -> posisi nempel legenda
    measureLayout() {
      this.contentWidth = this.$el ? this.$el.getBoundingClientRect().width : 0;
      this.barHeight = this.$refs.barWrap ? this.$refs.barWrap.offsetHeight : 0;
    },
    async onApplyFilter() {
      this.syncFilterUrl();
      await this.load();
    },
    onRightsChange(next) {
      this.rights = next;
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${rightsSearch(window.location.search, this.rights)}`);
      this.load();
    },
    // Buang kolom pembanding cacat, urut: custom rusak -> kapal ilang -> sama kayak Base -> kembar sama kolom sebelumnya yang lolos.
    applyRightGuards(leftAsset) {
      const reasons = [];
      const kept = [];
      const passedSignatures = [];
      this.rights.forEach((right) => {
        if (!isValidCustomRange(right.period)) {
          reasons.push('invalid date range');
          return;
        }
        const massId = right.massId === null ? leftAsset.massId : right.massId;
        const asset = (this.assets || []).find((a) => String(a.massId) === String(massId));
        if (!asset) {
          reasons.push('vessel no longer available');
          return;
        }
        const range = resolveRightRange(right.period, this.leftRange);
        if (sameSide({ massId: leftAsset.massId, range: this.leftRange }, { massId: asset.massId, range })) {
          reasons.push('identical to Base');
          return;
        }
        if (passedSignatures.some((sig) => sameSide(sig, { massId: asset.massId, range }))) {
          reasons.push('duplicate of another column');
          return;
        }
        passedSignatures.push({ massId: asset.massId, range });
        kept.push(right);
      });

      if (kept.length === this.rights.length) {
        this.compareNotice = '';
        return;
      }
      this.rights = kept;
      const distinctReasons = Array.from(new Set(reasons));
      const count = reasons.length;
      const noun = count === 1 ? 'column was' : 'columns were';
      this.compareNotice = `${count} comparison ${noun} removed — ${distinctReasons.join(', ')}.`;
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${rightsSearch(window.location.search, this.rights)}`);
    },
    async load() {
      this.token += 1;
      const token = this.token;
      this.isLoading = true;
      this.nowMs = Date.now();

      const leftAsset = this.leftAsset;
      if (!leftAsset) {
        this.baseResult = null;
        this.comparisonResults = [];
        this.isLoading = false;
        return;
      }

      this.applyRightGuards(leftAsset);

      const leftRange = this.leftEffectiveRange;
      const comparisonAssets = this.comparisonAssets;
      const comparisonEffRanges = this.comparisonEffectiveRanges;

      const [baseRes, ...comparisonRes] = await Promise.all([
        fetchSide(leftAsset, leftRange, this.nowMs),
        ...comparisonAssets.map((asset, i) => (asset ? fetchSide(asset, comparisonEffRanges[i], this.nowMs) : Promise.resolve(null))),
      ]);

      if (token !== this.token) return;
      this.baseResult = baseRes;
      this.comparisonResults = comparisonRes;
      this.isLoading = false;
    },
    emptyTextFor(group) {
      return this.notServedKeys.has(group.key) ? 'Not served by the summary endpoint yet.' : 'No data for this period.';
    },
  },
};
</script>

<style scoped>
.summary-compare {
  padding: 12px 16px;
}
.summary-compare__band {
  margin-inline: -16px;
  padding: 6px 16px;
  background: #f3f7fd;
}
.summary-compare__band--pinned {
  position: sticky;
  /* di bawah DataFilter (1020) biar date picker filter tetap nutupin */
  z-index: 1019;
}
/* legenda di bawah bar biar dropdown/picker slot gak ketutup */
.summary-compare__band--legend {
  z-index: 1018;
  padding-top: 0;
}
</style>
